import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { appointmentsApi, assignableUsersApi } from '../lib/api'
import type {
  AccountAnchor,
  AppointmentListQuery,
  AppointmentPayload,
  AppointmentUpdatePayload,
} from '../lib/api'

function anchorKey(anchor: AccountAnchor | undefined): readonly unknown[] {
  return ['account-appointments', anchor?.kind, anchor?.id]
}

function activityKey(anchor: AccountAnchor | undefined): readonly unknown[] {
  return ['account-activity', anchor?.kind, anchor?.id]
}

function invalidateAppointments(queryClient: ReturnType<typeof useQueryClient>, anchor?: AccountAnchor) {
  if (anchor) {
    queryClient.invalidateQueries({ queryKey: anchorKey(anchor) })
    queryClient.invalidateQueries({ queryKey: activityKey(anchor) })
  }
  queryClient.invalidateQueries({ queryKey: ['my-appointments'] })
  queryClient.invalidateQueries({ queryKey: ['account-appointments'] })
  queryClient.invalidateQueries({ queryKey: ['account-activity'] })
}

export function useAppointmentSettings(enabled = true) {
  return useQuery({
    queryKey: ['appointment-settings'],
    queryFn: appointmentsApi.settings,
    enabled,
  })
}

export function useMyAppointments(query: AppointmentListQuery) {
  return useQuery({
    queryKey: ['my-appointments', query],
    queryFn: () => appointmentsApi.listMine(query),
  })
}

export function useAccountAppointments(anchor: AccountAnchor | undefined) {
  return useQuery({
    queryKey: anchorKey(anchor),
    queryFn: () => {
      if (!anchor) throw new Error('Appointment anchor is required')
      return appointmentsApi.listForAnchor(anchor)
    },
    enabled: !!anchor?.id,
  })
}

export function useAccountActivity(anchor: AccountAnchor | undefined) {
  return useQuery({
    queryKey: activityKey(anchor),
    queryFn: () => {
      if (!anchor) throw new Error('Activity anchor is required')
      return appointmentsApi.activity(anchor)
    },
    enabled: !!anchor?.id,
  })
}

export function useAppointmentAssignees(enabled: boolean) {
  return useQuery({
    queryKey: ['appointment-assignees'],
    queryFn: assignableUsersApi.list,
    enabled,
  })
}

export function useCreateAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ anchor, payload }: { anchor: AccountAnchor; payload: AppointmentPayload }) =>
      appointmentsApi.create(anchor, payload),
    onSuccess: (_data, { anchor }) => invalidateAppointments(queryClient, anchor),
  })
}

export function useUpdateAppointment(anchor?: AccountAnchor) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ appointmentId, payload }: { appointmentId: string; payload: AppointmentUpdatePayload }) =>
      appointmentsApi.update(appointmentId, payload),
    onSuccess: () => invalidateAppointments(queryClient, anchor),
  })
}

export function useRescheduleAppointment(anchor?: AccountAnchor) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ appointmentId, startsAt }: { appointmentId: string; startsAt: string }) =>
      appointmentsApi.reschedule(appointmentId, startsAt),
    onSuccess: () => invalidateAppointments(queryClient, anchor),
  })
}

export function useCancelAppointment(anchor?: AccountAnchor) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (appointmentId: string) => appointmentsApi.cancel(appointmentId),
    onSuccess: () => invalidateAppointments(queryClient, anchor),
  })
}

export function useCompleteAppointment(anchor?: AccountAnchor) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (appointmentId: string) => appointmentsApi.complete(appointmentId),
    onSuccess: () => invalidateAppointments(queryClient, anchor),
  })
}

export function useNoShowAppointment(anchor?: AccountAnchor) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (appointmentId: string) => appointmentsApi.noShow(appointmentId),
    onSuccess: () => invalidateAppointments(queryClient, anchor),
  })
}
